// app/core.js — extracted from App.js (behaviour-preserving split).
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "@/components/ui/sonner";
import { getInstallCompletedAt, hasLocalSnapshot } from "@/lib/localDb";
import { refreshOfflineSession } from "@/lib/offlineAuth";
import { crumb as crashCrumb, installCrashReporter, reportClientError } from "@/lib/crashReporter";
import { isStandaloneLaunch } from "@/lib/device";
import {
  ROLE_SESSION_KEYS,
  forEachStoredSession,
  readRoleSession,
  readRoleSessionRaw,
  removeRoleSession,
  writeRoleSession,
} from "@/lib/authStorage";

/**
 * Tab-isolated theme context. We deliberately do NOT use next-themes'
 * built-in localStorage because localStorage writes fire `storage` events
 * across ALL tabs on the same browser, which caused dark/light mode
 * toggles to leak between dashboards (e.g. switching kitchen to dark also
 * flipped bar to dark on the same machine).
 *
 * sessionStorage is per-tab (not propagated via storage events to other
 * tabs, even duplicated ones once they diverge), so persisting the theme
 * there gives us: per-tab independence, refresh persistence, and zero
 * cross-tab leakage.
 */
const ThemeCtx = createContext({ theme: "light", resolvedTheme: "light", setTheme: () => {} });

export function ThemeProvider({ children, defaultTheme = "light" }) {
  const [theme, setThemeState] = useState(defaultTheme);
  const mountedRef = useRef(false);
  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("__rsm_theme");
      if (stored === "dark" || stored === "light") setThemeState(stored);
    } catch {}
    mountedRef.current = true;
  }, []);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.classList.toggle("light", theme === "light");
    root.style.colorScheme = theme;
    if (mountedRef.current) {
      try { window.sessionStorage.setItem("__rsm_theme", theme); } catch {}
    }
  }, [theme]);
  const value = useMemo(() => ({ theme, resolvedTheme: theme, setTheme: setThemeState }), [theme]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() { return useContext(ThemeCtx); }

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const DEFAULT_COVER =
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw4fHxyZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2NTA3NjE5fDA&ixlib=rb-4.1.0&q=85";
// Session 71 — storage keys + all token reads/writes live in lib/authStorage.
export const SESSION_KEYS = ROLE_SESSION_KEYS;
const PRICE_FILTERS = [
  { value: "all", label: "All prices" },
  { value: "under10", label: "Under R 10.00" },
  { value: "10to20", label: "R 10.00 - R 20.00" },
  { value: "over20", label: "Over R 20.00" },
];

export const http = axios.create({
  baseURL: API,
  // Session 27.28 — Hard timeout ceiling. Previously any request
  // could hang indefinitely if Cloudflare stalled between the CDN
  // and origin. That directly caused "Loading, loading..." stuck
  // states on the staff dashboard and made sign-in appear frozen.
  // 20s is generous enough for the initial snapshot bootstrap on a
  // slow connection while guaranteeing nothing hangs forever.
  timeout: 20000,
});

// Session 27.28 — Global GET retry interceptor. Cloudflare 520/521/522
// errors and short network hiccups now automatically retry ONE time
// after 500ms before propagating to the caller. This masks the vast
// majority of transient origin/CDN glitches so users don't see the
// "Loading" state get stuck or an "Invalid session" toast simply
// because ONE request out of 20 flapped.
//
// Session 27.30 — Extended to also retry POST/PUT/PATCH when the
// request NEVER REACHED THE SERVER (i.e. `!error.response`, meaning
// axios/network fetch threw before receiving any headers). That's
// safe because the server never processed the request — a retry
// cannot cause a duplicate write. We still do NOT retry POST on
// 5xx responses (the server MAY have processed it before the error
// was emitted; a retry could create duplicates).
//
// Rules:
//   • GET  → retry once on any transient error (5xx / network / timeout).
//   • POST/PUT/PATCH → retry once ONLY if the network never delivered
//     the request (no response received). This covers Cloudflare
//     "525 handshake failed" / DNS blip / connection reset.
//   • 4xx never retries (auth/permission errors won't heal).
//   • Writes with a response.status of 5xx are NOT retried (server
//     may have partially applied the write).
http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (!config) return Promise.reject(error);
    const method = String(config.method || "get").toLowerCase();
    const status = error?.response?.status;
    const hasResponse = !!error?.response;
    const isTimeout = error?.code === "ECONNABORTED";
    const isNetworkFail = !hasResponse; // includes timeouts, DNS, TLS
    const isReadonly = method === "get";
    let shouldRetry = false;
    if (isReadonly) {
      // GETs can safely retry any transient error.
      shouldRetry = !status || status >= 500 || isTimeout;
    } else {
      // Writes only retry when the request truly never reached the
      // server. NEVER retry a 5xx response on a write.
      shouldRetry = isNetworkFail;
    }
    // Session 47 — every failed WRITE that matters to live service is
    // reported to the platform error log: 5xx / network failures on any
    // write, and 4xx on the order / payment / void / stock endpoints
    // (a waiter who could not ring up, pay or void a bill).
    const reportWriteFailure = (err) => {
      try {
        if (isReadonly) return;
        const url = String(config.url || "");
        if (url.includes("/client-errors") || url.includes("/auth/")) return;
        const st = err?.response?.status;
        const critical = /\/(payments?|void|void-line|items|table-sessions|retail\/sales|orders|receive|stock|inventory|events\/sync)/.test(url);
        if (!(st == null || st >= 500 || (critical && st >= 400 && st !== 401))) return;
        const detail = err?.response?.data?.detail;
        // Session 56 — an action on a `local-…` order that is still syncing
        // is an expected, self-healing race (the app queues it locally),
        // not a fault worth an alert email.
        if (st === 409 && /still syncing/i.test(String(typeof detail === "string" ? detail : ""))) return;
        // Session 62 — not faults: `preflight` is a soft pre-check the app
        // ignores on any failure, and `mark-served` / `mark-ready` fall
        // back to the offline queue on timeouts / 5xx (the tap is never
        // lost). Both were filling the Admin log during server restarts.
        if (url.includes("/orders/preflight")) return;
        if ((st == null || st >= 500) && /\/(mark-served|mark-ready|mark-delivered)/.test(url)) return;
        // Session 68 — BEST-EFFORT fast paths (open table, record payment)
        // are already written to the local event queue before the HTTP
        // call; a timeout / no-response here only means the sync loop
        // delivers it a few seconds later. Not a fault — don't email it.
        if (config.bestEffort && st == null) return;
        const detailText = typeof detail === "string" ? detail : (detail ? JSON.stringify(detail).slice(0, 300) : (err?.message || "network error"));
        reportClientError("api", new Error(`${method.toUpperCase()} ${url} → ${st ?? "no response"}: ${detailText}`), { route: window.location.pathname });
      } catch (_) { /* noop */ }
    };
    if (!shouldRetry) { reportWriteFailure(error); return Promise.reject(error); }
    // Session 69 — soft pre-checks opt out of the automatic retry: a
    // retried 6 s timeout became a 12 s freeze on "Place order".
    if (config.noRetry) { reportWriteFailure(error); return Promise.reject(error); }
    if (config.__nfcsRetried) { reportWriteFailure(error); return Promise.reject(error); }
    config.__nfcsRetried = true;
    await new Promise((r) => setTimeout(r, 500));
    try {
      return await http.request(config);
    } catch (retryErr) {
      reportWriteFailure(retryErr);
      return Promise.reject(retryErr);
    }
  }
);

// ─────────────────────────────────────────────────────────────────
// Developer-tools: simulated offline / cloud-disconnect interceptors
// ─────────────────────────────────────────────────────────────────
// These two localStorage keys let operators (and support) deterministically
// exercise offline code paths WITHOUT toggling the device radio:
//
//   • "nfcs:dev:simulate-offline"          — reject EVERY http request
//   • "nfcs:dev:simulate-cloud-disconnect" — reject only /api/* requests
//                                            (static/CDN assets pass)
//
// The interceptor synthesises a rejected promise shaped like a real
// network failure (axios "Network Error" / code "ERR_NETWORK") so every
// catch block downstream — including pingConnectivity() and the offline
// auth fallback — behaves identically to a true network outage. When
// both keys are unset the interceptor is a no-op, so production
// installations are never affected.
export const DEV_SIMULATE_OFFLINE_KEY = "nfcs:dev:simulate-offline";
export const DEV_SIMULATE_CLOUD_KEY = "nfcs:dev:simulate-cloud-disconnect";

function _shouldSimulateFailure(config) {
  try {
    if (typeof localStorage === "undefined") return null;
    if (localStorage.getItem(DEV_SIMULATE_OFFLINE_KEY) === "1") return "offline";
    if (localStorage.getItem(DEV_SIMULATE_CLOUD_KEY) === "1") {
      const url = String(config?.url || "");
      const full = url.startsWith("http")
        ? url
        : `${(config?.baseURL || "")}${url}`;
      if (/\/api(\/|$)/.test(full)) return "cloud";
    }
  } catch (_) { /* ignore */ }
  return null;
}

function _simulatedError(config, reason) {
  const err = new Error(
    reason === "offline"
      ? "Network Error (simulated offline)"
      : "Network Error (simulated cloud disconnect)",
  );
  err.code = "ERR_NETWORK";
  err.isAxiosError = true;
  err.config = config;
  err.request = { simulated: true, reason };
  return err;
}

const _devRequestInterceptor = (config) => {
  const reason = _shouldSimulateFailure(config);
  if (reason) return Promise.reject(_simulatedError(config, reason));
  return config;
};

http.interceptors.request.use(_devRequestInterceptor);
axios.interceptors.request.use(_devRequestInterceptor);

// Also intercept window.fetch so anything that bypasses axios (e.g. the
// pingConnectivity() helper in offlineAuth.js) also sees a simulated
// outage. We only wrap once — hot-reload safe.
if (typeof window !== "undefined" && !window.__nfcsDevFetchWrapped) {
  const _origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (_origFetch) {
    window.fetch = (input, init) => {
      try {
        if (localStorage.getItem(DEV_SIMULATE_OFFLINE_KEY) === "1") {
          return Promise.reject(new TypeError("Failed to fetch (simulated offline)"));
        }
        if (localStorage.getItem(DEV_SIMULATE_CLOUD_KEY) === "1") {
          const url = typeof input === "string" ? input : (input && input.url) || "";
          if (/\/api(\/|$)/.test(String(url))) {
            return Promise.reject(new TypeError("Failed to fetch (simulated cloud disconnect)"));
          }
        }
      } catch (_) { /* localStorage unavailable — just proceed */ }
      return _origFetch(input, init);
    };
    window.__nfcsDevFetchWrapped = true;
  }
}

/**
 * Session 31.11 — Payment split rule: CARD NEVER GIVES CHANGE.
 * Given what was tendered (cash, card), the amount due and the tip the
 * cashier typed, return { cash, card, tip, change }:
 *  • any card amount beyond what was still owed after cash is a tip;
 *  • a typed tip that is larger than that is honoured — on a card-only
 *    payment the card charge grows to cover it, in a cash payment it is
 *    taken out of the cash overpayment;
 *  • only cash can come back as change.
 */
const settleTender = ({ cash = 0, card = 0, due = 0, tipDraft = 0 }) => {
  const c = Math.max(0, Number(cash) || 0);
  let k = Math.max(0, Number(card) || 0);
  const d = Math.max(0, Number(due) || 0);
  const typed = Math.max(0, Number(tipDraft) || 0);
  const owedAfterCash = Math.max(0, d - c);
  const cardOver = Math.max(0, k - owedAfterCash);      // card beyond what was owed → tip
  let tip = Math.max(typed, cardOver);
  const paid = c + k;
  const overpay = Math.max(0, paid - d);
  if (c > 0 && k === 0) {
    // cash only: tip comes out of the overpayment, rest is change
    tip = Math.min(typed, overpay);
  } else if (tip > overpay) {
    // typed tip exceeds what was tendered → put the difference on the card
    k += tip - overpay;
  }
  const change = Math.max(0, (c + k) - d - tip);
  const r2 = (n) => Math.round(n * 100) / 100;
  return { cash: r2(c), card: r2(k), tip: r2(tip), change: r2(change) };
};

// Session 31.26 — Tailwind JIT safelist for the tender tone classes that the
// split-pay dialog composes at runtime (`border-${tone}-500/40` …). Tailwind
// only emits classes it can see literally in the source, so keep this list.
// eslint-disable-next-line no-unused-vars
const _TENDER_TONE_SAFELIST = [
  "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-500/20 focus-visible:ring-emerald-500 dark:text-emerald-300 bg-emerald-600 hover:bg-emerald-600/90",
  "border-sky-500/40 bg-sky-500/10 text-sky-700 hover:border-sky-500 hover:bg-sky-500/20 focus-visible:ring-sky-500 dark:text-sky-300 bg-sky-600 hover:bg-sky-600/90",
  "border-teal-500/40 bg-teal-500/10 text-teal-700 hover:border-teal-500 hover:bg-teal-500/20 focus-visible:ring-teal-500 dark:text-teal-300 bg-teal-600 hover:bg-teal-600/90",
];

export const formatPrice = (value) => {
  const numeric = Number(value || 0);
  const [whole, decimal] = numeric.toFixed(2).split(".");
  return `R ${Number(whole).toLocaleString("en-US")}.${decimal}`;
};

// ─────────────────────────────────────────────────────────────────
// Session 27.17 — postWithRetry: resilient POST with exponential
// backoff for endpoints where transient 5xx / network errors would
// otherwise silently break user actions.
//
// Real-world trigger: production deployments behind Cloudflare
// occasionally emit 520 "could not parse" errors when the origin
// hiccups. Every one of those errors used to kill a "Mark Delivered"
// ack, leaving orders stuck as "confirmed" in the DB. Staff would
// tap the button dozens of times to no effect. This helper retries
// the POST up to 3 times with 400ms / 900ms / 2000ms backoff before
// bubbling the failure up to the caller.
//
// Rules:
//   • Retry on network errors (no response) and 5xx status codes.
//   • Do NOT retry on 4xx (client errors — retrying won't help).
//   • Total worst-case time: ~3.3s (better than a stuck order).
// ─────────────────────────────────────────────────────────────────
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export async function postWithRetry(path, body, config, { attempts = 3, delays = [400, 900, 2000] } = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await http.post(path, body, config);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const isTransient = !status || status >= 500;
      // 4xx errors won't get better by retrying — fail fast.
      if (!isTransient) throw err;
      // Ran out of retries — surface the error.
      if (attempt >= attempts - 1) throw err;
      // Otherwise wait then retry.
      await _sleep(delays[attempt] || 1000);
    }
  }
  throw lastErr;
}

/**
 * Session 54 — "Is the cloud the problem (not my request)?"
 *
 * A kitchen / bar / runner status change (READY, DELIVERED) must NEVER be
 * lost. Until now only "no response" (true offline / timeout) fell back
 * to the local event queue; an ERROR RESPONSE from the cloud (500,
 * 502/503/504 database-outage replies, Cloudflare 520-530, 408/429) was
 * treated as a hard failure, shown as a small toast and DROPPED. During a
 * 20-minute database wobble that meant: kitchen tapped Ready → LAN told
 * the waiter → waiter delivered → cloud never heard any of it → when it
 * recovered the ticket re-appeared un-ready on the kitchen screen.
 * Every server / edge error is transient from the device's point of
 * view, so it must take the same offline path (the server reducers are
 * idempotent — replaying is always safe). 4xx business errors (404 order
 * gone, 400 invalid) are NOT transient and still surface to the user.
 */
export function isTransientCloudError(error) {
  if (!error) return false;
  if (!error.response) return true; // network / timeout / DNS / TLS
  const st = Number(error.response.status) || 0;
  if (st >= 500) return true;      // 500, 502, 503 (DB unavailable), 504, 520-530 (Cloudflare edge)
  if (st === 408 || st === 429) return true;
  return false;
}


export const getOrderTimeLabel = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  return `${format(date, "p")} · ${formatDistanceToNow(date, { addSuffix: true })}`;
};

// Session 30.2 — hard session lifetime. Every dashboard signs out
// automatically 24 hours after login, per the operator's request.
const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Session 46 — the 24 h cap must never fire WHILE the device is being
// used (reported as "staff get kicked out mid-rush"). We track the last
// user interaction on the device and only let the cap expire a session
// once the device has been idle for this long. Server-side expiry is
// already sliding (every request extends it), so this is purely the
// client-side cap being made shift-friendly.
const SESSION_IDLE_BEFORE_EXPIRE_MS = 15 * 60 * 1000;
const LAST_ACTIVITY_KEY = "nfcs:last_activity";
let _lastActivityMem = 0;
function _touchActivity() {
  const now = Date.now();
  // Throttle storage writes to once per 5 s.
  if (now - _lastActivityMem < 5000) return;
  _lastActivityMem = now;
  try { window.localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); } catch (_) { /* noop */ }
}
function _lastActivityAt() {
  if (_lastActivityMem) return _lastActivityMem;
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch (_) { return 0; }
}
/** True when the 24 h cap has passed AND the device has been idle long
 *  enough that signing out will not interrupt anyone. */
export function _sessionAgeExpired(loginAtMs) {
  if (!Number.isFinite(loginAtMs)) return false;
  const now = Date.now();
  if (now - loginAtMs <= SESSION_MAX_AGE_MS) return false;
  const idleFor = now - _lastActivityAt();
  return idleFor >= SESSION_IDLE_BEFORE_EXPIRE_MS;
}
if (typeof window !== "undefined") {
  try {
    ["pointerdown", "keydown", "touchstart"].forEach((evt) => window.addEventListener(evt, _touchActivity, { passive: true, capture: true }));
    _touchActivity();
  } catch (_) { /* noop */ }
}

export const readStorage = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getSession = (role) => {
  // Session 26 — lazy heartbeat check. On a fresh page load we clear
  // any PENDING sessions (see `_clearPendingRoleSessions`) so a
  // dashboard crash from a previous tab-life can't be resurrected.
  // Session 30.2 — the fresh-launch check now clears ALL sessions
  // (tab-close = sign-out, per the operator's request); refreshes keep
  // the session because the heartbeat is still fresh.
  try { _ensureFreshSessionOrClear(); } catch (_) { /* noop */ }
  // Prefer the tab-scoped session (most recent login on this tab).
  // Fall back to the device-scoped store so a page refresh recovers
  // the confirmed session even after sessionStorage is dropped.
  const session = readRoleSession(role, "any");
  if (!session) return session;
  // Session 30.2 — 24-hour session TTL, enforced lazily on every read.
  // Sessions older than 24h are wiped and the caller sees "logged out".
  // Legacy sessions without a login stamp get stamped now so they
  // expire 24h from this deploy rather than instantly.
  try {
    if (session._login_at) {
      const loginAtMs = new Date(session._login_at).getTime();
      // Session 46 — idle-aware: never expire a session that is in use.
      if (_sessionAgeExpired(loginAtMs)) {
        clearSession(role, "session_max_age_24h");
        return null;
      }
    } else if (typeof session === "object") {
      session._login_at = new Date().toISOString();
      const stamped = JSON.stringify(session);
      if (readRoleSessionRaw(role, "tab")) writeRoleSession(role, stamped, "tab");
      if (readRoleSessionRaw(role, "device")) writeRoleSession(role, stamped, "device");
    }
  } catch (_) { /* noop */ }
  return session;
};

/**
 * Promote a pending session to fully-confirmed. Called from each
 * dashboard's boot effect after the first successful render.
 * Idempotent — safe to call on every mount.
 *
 * The promotion moves the session out of tab-scoped sessionStorage
 * into device-scoped localStorage so page refresh and tab-reopen
 * both preserve the login. This matches the local-first restaurant
 * philosophy — the device IS the session, not the tab.
 */
export const confirmSession = (role) => {
  try {
    // Prefer the tab-scoped copy — it's the freshest one written by the
    // login flow. Fall back to any already-confirmed device-scoped copy
    // (e.g. after a page refresh).
    const parsed = readRoleSession(role, "any");
    if (!parsed) return;
    delete parsed._pending;
    delete parsed._pending_since;
    // Promote to device scope and keep the tab copy in sync so
    // getSession() sees the same shape regardless of store.
    writeRoleSession(role, parsed, "both");
  } catch (_) { /* noop */ }
};

export const clearSession = (role, reason = "unspecified") => {
  const hadSession = !!readRoleSessionRaw(role, "any");
  removeRoleSession(role, "both");
  // Session 45 — a client reported "staff get kicked out while ringing
  // up". Every sign-out that the USER did not ask for is now logged to
  // the server (Developer tools → Crash reports, kind "manual") with the
  // reason and the code path that triggered it, so the next occurrence
  // can be traced instead of guessed at.
  if (hadSession && reason !== "user_logout") {
    try {
      const err = new Error(`Signed out (${role}): ${reason}`);
      reportClientError("manual", err, { route: window.location.pathname });
      crashCrumb(`signout ${role}: ${reason}`);
    } catch (_) { /* noop */ }
  }
};

// Session 27.110 — Streak-protected 401 handling for ALL dashboards.
//
// Production incident: during high-load service (dozens of tables
// active), staff / kitchen / bar / owner were sporadically kicked
// out of their dashboards. Root cause: any SINGLE 401 response
// (which can be triggered by a transient Cloudflare glitch, a WS
// reconnect race, an in-flight request from a torn-down component,
// or a backend hiccup) was immediately clearing the session. This
// helper enforces "at least N consecutive 401s within WINDOW ms
// before we accept the token is really dead". Staff already had
// this protection (Session 27.24) but owner/kitchen/bar did not.
// This module-level cache makes the same guard available to all
// roles without prop-drilling refs through every handler.
const _unauthorizedStreaks = new Map(); // role → { count, at }
export function shouldKickOutOn401(role, threshold = 2, windowMs = 12000) {
  try {
    const now = Date.now();
    const state = _unauthorizedStreaks.get(role) || { count: 0, at: 0 };
    if (now - state.at > windowMs) state.count = 0;
    state.count += 1;
    state.at = now;
    _unauthorizedStreaks.set(role, state);
    if (state.count < threshold) return false;
    _unauthorizedStreaks.set(role, { count: 0, at: 0 });
    return true;
  } catch (_) {
    return true; // fail-safe: kick out if the guard itself blows up
  }
}

// Session 46 — OFFLINE SESSION UPGRADE. A device that signed in while the
// internet was down holds an `offline:` token. That token is NOT a server
// bearer: the moment connectivity returns every API call 401s, the
// streak guard trips, the /auth/me confirmation also 401s and the waiter
// is thrown back to the login screen mid-order ("randomly kicked out").
// `refreshOfflineSession` (POST /auth/offline/refresh) was built for
// exactly this hand-over but was never wired in. This helper exchanges
// the offline token for a real session, preserving the login stamp so
// the 24 h cap still counts from the original sign-in.
const _offlineUpgradeInFlight = new Map(); // role → Promise
export function isOfflineToken(token) {
  return typeof token === "string" && token.startsWith("offline:");
}
async function upgradeOfflineSession(role, current) {
  if (!current || !isOfflineToken(current.token)) return null;
  if (_offlineUpgradeInFlight.get(role)) return _offlineUpgradeInFlight.get(role);
  const run = (async () => {
    try {
      const userId = current?.user?.id;
      if (!userId) return null;
      const fresh = await refreshOfflineSession(userId);
      if (!fresh || !fresh.token) return null;
      const next = {
        ...current,
        ...fresh,
        mode: "online",
        _login_at: current._login_at || new Date().toISOString(),
        _upgraded_from_offline_at: new Date().toISOString(),
      };
      delete next._pending;
      delete next._pending_since;
      writeRoleSession(role, next, "both");
      try { crashCrumb(`offline session upgraded (${role})`); } catch (_) { /* noop */ }
      return next;
    } catch (_) {
      return null;
    } finally {
      _offlineUpgradeInFlight.delete(role);
    }
  })();
  _offlineUpgradeInFlight.set(role, run);
  return run;
}

/**
 * Hook used by every dashboard: while the active session is an offline
 * one, try to upgrade it whenever the browser reports connectivity (on
 * mount, on the `online` event and every 20 s). `apply(next)` receives
 * the upgraded session so the dashboard can swap its in-memory state
 * (and therefore its Authorization header) without a reload.
 */
export function useOfflineSessionUpgrade(role, session, apply) {
  const token = session?.token;
  useEffect(() => {
    if (!isOfflineToken(token)) return undefined;
    let cancelled = false;
    const attempt = async () => {
      if (cancelled) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const next = await upgradeOfflineSession(role, session);
      if (next && !cancelled) {
        try { apply(next); } catch (_) { /* noop */ }
        try { toast.success("Back online — your sign-in was refreshed automatically."); } catch (_) { /* noop */ }
      }
    };
    attempt();
    const id = window.setInterval(attempt, 20000);
    window.addEventListener("online", attempt);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("online", attempt);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, token]);
}

// Session 27.110 — Confirmation ping before actually clearing a
// session. Fires a lightweight /auth/me with the current token; if
// that returns 200 the 401 storm was transient (Cloudflare hiccup,
// backend micro-restart, in-flight retry) and we should leave the
// session alone. Only a genuine 401 from this dedicated call
// triggers `onDead()`.
const _authConfirmInFlight = new Map(); // role → boolean
export async function confirmSessionDeadThen(role, token, onDead) {
  try {
    if (!token) return;
    // Session 46 — an offline token ALWAYS 401s on the server; that is not
    // a dead session, it is one that needs upgrading. Never kick here —
    // the dashboard's useOfflineSessionUpgrade hook handles the hand-over.
    if (isOfflineToken(token)) return;
    if (_authConfirmInFlight.get(role)) return;
    _authConfirmInFlight.set(role, true);
    try {
      await http.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
      // Token is fine — swallow.
    } catch (err) {
      if (err?.response?.status === 401) {
        try { onDead(); } catch (_) { /* noop */ }
      }
    } finally {
      _authConfirmInFlight.set(role, false);
    }
  } catch (outerErr) {
    // Any unexpected failure → be safe, still call onDead so the
    // user isn't stranded on a broken dashboard forever.
    console.warn("[auth] confirmAuth outer failure:", outerErr?.message || outerErr);
    try { onDead(); } catch (e) { console.warn("[auth] onDead callback failed:", e?.message || e); }
  }
}

// ─────────────────────────────────────────────────────────────────────
// Tab-close beacon logout

/**
 * useAutoInstall — safety net that guarantees Stage 2 (business
 * synchronisation) runs even when the user's entry path bypasses the
 * normal login → install/setup redirect.
 *
 * Scenarios this catches:
 *  • A session token exists from an earlier build that predates the
 *    install flow (e.g. Session 24 users upgrading in place).
 *  • The user signed in via the legacy business-password path, which
 *    doesn't have a PIN to derive offline credentials from.
 *  • The user navigated directly to /staff, /kitchen, /bar or /owner
 *    without going through the RoleLoginPage.
 *
 * Each dashboard component calls this hook on mount. If a valid session
 * is present AND the local database has no snapshot yet, we redirect
 * the operator to `/install/setup?next=<current path>` so they see the
 * full sync progress before their dashboard opens.
 */
export function useAutoInstall(session) {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!session?.token) return undefined;
    // Offline-signed-in sessions can't run install/setup — they'd
    // need the network anyway. Skip the guard until reconnect.
    if (String(session.token).startsWith("offline:")) return undefined;
    // Never redirect if we're already inside the install flow —
    // prevents any theoretical redirect loop on Android WebViews
    // where a partial IDB failure caused hasLocalSnapshot() to
    // repeatedly return false.
    const path = location.pathname || "";
    if (path.startsWith("/install")) return undefined;
    // Session 25.10 — only force the offline installation when the
    // app is running as an INSTALLED PWA (display-mode: standalone).
    // In a regular browser tab we should never auto-download the
    // full business database; a curious owner might just be
    // previewing the app from their laptop and would not expect a
    // multi-megabyte silent download on every dashboard visit.
    let isInstalled = false;
    try { isInstalled = isStandaloneLaunch(); } catch (_) { isInstalled = false; }
    if (!isInstalled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Session 27.62 — Check the install-completed flag FIRST.
        // If the device has already run the setup wizard once, we
        // trust `getInstallCompletedAt()` (localStorage-backed) and
        // skip the safety-net redirect entirely. This is what stops
        // the wizard from re-appearing after every login on APK
        // WebViews where IDB meta writes silently drop.
        const installedAt = await getInstallCompletedAt();
        if (cancelled) return;
        if (installedAt) return;
        const primed = await hasLocalSnapshot();
        if (cancelled) return;
        if (!primed) {
          const next = encodeURIComponent(path + (location.search || ""));
          navigate(`/install/setup?next=${next}`, { replace: true });
        }
      } catch (_) { /* readMeta failure is non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [session?.token, navigate, location.pathname, location.search]);
}

// ─────────────────────────────────────────────────────────────────────
// When the user closes the tab / navigates away, fire a sendBeacon to
// /auth/logout-beacon for every active session token stored in
// sessionStorage. This deletes the server-side session immediately so
// the "hide already-signed-in users" filter releases them from the
// login picker on their next attempt. sendBeacon can't set an
// Authorization header, so we pass the token in the JSON body.
// Session 26 — the pagehide "logout beacon" is REMOVED.
//
// Historical context: we used to fire a `POST /api/auth/logout-beacon`
// from a `pagehide` listener so a tab-close would release the server
// session (and thus release the picker's "hide already-signed-in
// users" filter for that user on the next attempt).
//
// The problem: `pagehide` fires on both a genuine tab close AND on
// page reload (Ctrl+R / F5 / Android WebView activity restart / Chrome
// bfcache). There is no cross-browser way to distinguish the two
// inside the pagehide handler. So the beacon was invalidating the
// backend session on EVERY refresh, which made the token in
// localStorage instantly stale and forced the user back to the
// login screen — exactly the bug the user reported.
//
// Correct restaurant behaviour: a device stays signed in until the
// operator explicitly signs out. Kitchen and bar tablets go through
// dozens of screen-off / screen-on cycles per shift; a POS that logs
// out on every one of those is unusable.
//
// If we later want an idle-timeout policy, it should be an EXPLICIT
// timer inside the app (e.g. auto-lock after 30m of no interaction),
// not a lifecycle-triggered forced logout. That's a separate
// milestone \u2014 for now, no beacon.
//
// Keeping the sentinel `__nfcsBeaconInstalled` in place so any
// future re-introduction is opt-in behind a clear flag.
if (typeof window !== "undefined") {
  window.__nfcsBeaconInstalled = false;
}

// ─────────────────────────────────────────────────────────────────────
// Session heartbeat — detect "fresh launch" vs "refresh"
// ─────────────────────────────────────────────────────────────────────
// Chrome / installed-PWA lifecycles sometimes RESTORE sessionStorage
// even after the user closed the tab / app (session-restore, tab
// unfreeze, PWA app-instance reuse). That means a user who logged in
// as Owner and closed the tab would come back to find their session
// still alive — a genuine accountability bug.
//
// The fix: maintain a millisecond-precision heartbeat in sessionStorage
// while the tab is open. On page load, if the heartbeat is missing OR
// noticeably stale, we treat this as a fresh launch and clear ALL role
// sessions. Refresh keeps the session (heartbeat is only ~1s old), but
// close-and-reopen wipes it (heartbeat gap grows past the threshold).
const HEARTBEAT_KEY = "nfcs:session_heartbeat";
// 90s threshold: much longer than any refresh (1–3s) but short enough
// that a user who takes a coffee break, closes the tab, and comes back
// gets logged out.
const HEARTBEAT_STALE_MS = 90 * 1000;

export function _isSessionHeartbeatFresh() {
  try {
    const raw = window.sessionStorage.getItem(HEARTBEAT_KEY);
    if (!raw) return false;
    const last = Number(raw);
    if (!Number.isFinite(last) || last <= 0) return false;
    return (Date.now() - last) < HEARTBEAT_STALE_MS;
  } catch (_) {
    return false;
  }
}

export function _writeHeartbeat() {
  try { window.sessionStorage.setItem(HEARTBEAT_KEY, String(Date.now())); }
  catch (_) { /* noop */ }
}

export function _clearPendingRoleSessions() {
  // Session 26 originally only discarded *pending* sessions here, so
  // confirmed sessions survived a full tab close.
  //
  // Session 30.2 wiped ALL sessions (pending AND confirmed) whenever the
  // heartbeat was stale, to make "close the tab = sign out".
  //
  // Session 31.20 — REVERTED to pending-only. Tablets under memory
  // pressure SUSPEND / DISCARD background tabs: timers stop, the
  // heartbeat goes stale, and the moment the waiter switches back the
  // page reloads and every login was wiped — reported on the floor as
  // "waiters get kicked out when the system is busy". A confirmed login
  // now survives tab discards / app switches / soft reboots and only
  // ends on explicit logout or the 24 h SESSION_MAX_AGE_MS cap.
  try {
    forEachStoredSession((role, parsed, kind) => {
      if (!parsed || parsed._pending) removeRoleSession(role, kind);
      return false;
    });
  } catch (_) { /* noop */ }
}

// Ensures the very first `getSession` call in this page load clears
// any stale PENDING sessions. Idempotent — once we've written a
// fresh heartbeat, future calls become no-ops until the next launch.
let _freshCheckDone = false;
function _ensureFreshSessionOrClear() {
  if (_freshCheckDone) return;
  _freshCheckDone = true;
  if (!_isSessionHeartbeatFresh()) {
    _clearPendingRoleSessions();
  }
  _writeHeartbeat();
}

export const getCartKey = (tagCode) => `restaurant-smart-menu-cart-${tagCode}`;

// Global unhandled-error / unhandled-rejection recorder. On older
// Android WebViews these events would otherwise leave the app in a
// broken half-state with no explanation.
if (typeof window !== "undefined" && !window.__nfcsGlobalErrorHook) {
  window.__nfcsGlobalErrorHook = true;
  const push = (kind, err) => {
    try {
      const log = JSON.parse(window.localStorage.getItem("nfcs.errors") || "[]");
      log.unshift({
        at: new Date().toISOString(),
        kind,
        message: String((err && (err.message || err.reason)) || err || "").slice(0, 500),
        stack: (err && err.stack) ? String(err.stack).slice(0, 1500) : "",
        ua: (navigator && navigator.userAgent) || "",
      });
      window.localStorage.setItem("nfcs.errors", JSON.stringify(log.slice(0, 30)));
    } catch (_) { /* noop */ }
  };
  window.addEventListener("error", (e) => push("error", e.error || e));
  window.addEventListener("unhandledrejection", (e) => push("promise", e.reason));
  // Session 43 — remote crash reporting (server-side log per business).
  try { installCrashReporter(); } catch (_) { /* noop */ }
}
